import type { StaffAttentionSource, StaffAttentionSummary } from "@lafayette/schemas";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { useAreaHref } from "../navigation/use-offering";
import { Avatar } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { cn } from "../ui/cn";
import { Icon } from "../ui/icon";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { countText, itemArea, itemCodeText, itemLink, needsWord, sourceCopy } from "./labels";
import {
  ageText,
  isPartial,
  type NeedsReason,
  type NeedsRow,
  type NeedsTab,
  needsReasons,
  needsRows,
  sourcesForTab,
  unreadableForTab,
} from "./needs-you";

// "Needs you now" (CHT-2020): one row for each customer who waits on this staff member, built from the attention items (`needs-you.ts`). A
// row opens the conversation or the request. The counts and the links to the full lists stay under the rows: the API names only the first
// items of each list, so the lists are how a long queue is reached.

const tabLabels: Readonly<Record<NeedsTab, string>> = {
  all: "All",
  conversations: "Conversations",
  requests: "Requests",
};
const emptyText: Readonly<Record<NeedsTab, string>> = {
  all: "Nothing needs action now",
  conversations: "No conversation needs you now",
  requests: "No request needs you now",
};

/** The line under a customer name: the reason in words, and how many other reasons the customer has. */
function reasonLine({ reason, more }: Pick<NeedsRow, "reason" | "more">): string {
  const text =
    itemCodeText(reason.item.kind, reason.item.code) ??
    sourceCopy[reason.key].label(1).replace(/^./u, (letter) => letter.toUpperCase());
  return more === 0 ? text : `${text} · ${more} more`;
}

function ReasonPill({ reason }: Readonly<{ reason: NeedsReason }>) {
  const urgent = reason.severity === "urgent";
  return (
    <Badge
      tone={urgent ? "brass" : "neutral"}
      className="justify-self-start py-1 text-meta font-semibold"
    >
      <span
        aria-hidden="true"
        className={cn("size-1.5 rounded-full", urgent ? "bg-brass" : "bg-neutral-dot")}
      />
      {needsWord[reason.key]}
    </Badge>
  );
}

const rowClass =
  "grid min-h-[68px] grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3.5 gap-y-1 rounded-md px-3.5 py-3 text-foreground no-underline compact:min-h-12 compact:py-1.5 sm:grid-cols-[auto_minmax(0,1fr)_150px_44px_18px]";

function RowBody({ row, now }: Readonly<{ row: NeedsRow; now: string }>) {
  const age = ageText(row.reason.item.since, now);
  return (
    <>
      <Avatar
        id={row.customer.id}
        name={row.customer.displayName}
        className="row-span-2 sm:row-span-1"
      />
      <span className="grid min-w-0">
        <span className="font-serif text-lg leading-6 font-medium">{row.customer.displayName}</span>
        <span className="text-sm leading-5 text-muted-foreground">{reasonLine(row)}</span>
      </span>
      <span className="col-start-2 row-start-2 sm:col-auto sm:row-auto">
        <ReasonPill reason={row.reason} />
      </span>
      <span
        className={cn(
          "col-start-3 row-start-1 text-right text-sm font-semibold sm:col-auto sm:row-auto",
          row.reason.severity === "urgent" ? "text-brass-strong" : "text-muted-foreground",
        )}
      >
        {age}
      </span>
      <Icon name="arrow-right-01-round" className="hidden text-muted-foreground sm:block" />
    </>
  );
}

function EmptyRows({ tab }: Readonly<{ tab: NeedsTab }>) {
  return (
    <div role="status" className="grid gap-1 px-3.5 py-5">
      <p className="m-0 flex items-center gap-2 font-semibold">
        <Icon name="inbox" className="text-muted-foreground" />
        {emptyText[tab]}
      </p>
      <p className="m-0 text-sm leading-6 text-muted-foreground">
        {tab === "all"
          ? "No handoff, request or coverage gap waits for you."
          : "Switch to All to see everything that waits for you."}
      </p>
    </div>
  );
}

function NeedsList({ rows, now }: Readonly<{ rows: readonly NeedsRow[]; now: string }>) {
  const areaHref = useAreaHref();
  return (
    <ul className="m-0 grid list-none gap-0.5 p-0">
      {rows.map((row) => {
        const href = areaHref(itemArea(row.reason.item), itemLink(row.reason.item));
        return (
          <li key={row.customer.id}>
            {href === null ? (
              <div className={rowClass}>
                <RowBody row={row} now={now} />
              </div>
            ) : (
              <Link to={href} className={cn(rowClass, "hover:bg-accent")}>
                <RowBody row={row} now={now} />
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** The counts of the sources that need action, each a link to its full list (plain text while its area does not open). */
function FullLists({ sources }: Readonly<{ sources: readonly StaffAttentionSource[] }>) {
  const areaHref = useAreaHref();
  if (sources.length === 0) return null;
  return (
    <nav aria-label="Full lists" className="border-t border-hairline px-3.5 pt-3 pb-2">
      <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-sm">
        {sources.map((source) => {
          const copy = sourceCopy[source.key];
          const text = `${countText(source)} ${copy.label(source.count)}`;
          const href = areaHref(copy.area, copy.link);
          return (
            <li key={source.key}>
              {href === null ? (
                <span className="inline-flex min-h-11 items-center text-muted-foreground compact:min-h-8">
                  {text}
                </span>
              ) : (
                <Link
                  to={href}
                  className="inline-flex min-h-11 items-center text-link underline-offset-4 hover:underline compact:min-h-8"
                >
                  {text}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** What a tab could not read, in words. An unreadable source is never "none": the tab says so, with or without rows. */
function NotRead({
  sources,
  lead,
}: Readonly<{ sources: readonly StaffAttentionSource[]; lead: string }>) {
  return (
    <p role="note" className="m-0 px-3.5 py-3 text-sm leading-6 text-muted-foreground">
      {lead} Not read: {sources.map((source) => sourceCopy[source.key].label(0)).join(", ")}.
    </p>
  );
}

function NoRows({
  tab,
  unreadable,
  hasLists,
}: Readonly<{ tab: NeedsTab; unreadable: readonly StaffAttentionSource[]; hasLists: boolean }>) {
  if (unreadable.length > 0) {
    return (
      <NotRead sources={unreadable} lead="Nothing to show from the queues that could be read." />
    );
  }
  if (hasLists) {
    return (
      <p className="m-0 px-3.5 py-4 text-sm leading-6 text-muted-foreground">
        No customer is named here. Open a list below.
      </p>
    );
  }
  return <EmptyRows tab={tab} />;
}

function TabBody({
  summary,
  tab,
  lists,
}: Readonly<{
  summary: StaffAttentionSummary;
  tab: NeedsTab;
  lists: readonly StaffAttentionSource[];
}>) {
  const reasons = useMemo(() => needsReasons(summary), [summary]);
  const rows = useMemo(() => needsRows(reasons, tab), [reasons, tab]);
  const unreadable = unreadableForTab(summary, tab);
  if (rows.length === 0) {
    return (
      <NoRows tab={tab} unreadable={unreadable} hasLists={sourcesForTab(lists, tab).length > 0} />
    );
  }
  return (
    <>
      <NeedsList rows={rows} now={summary.generatedAt} />
      {unreadable.length > 0 ? (
        <NotRead sources={unreadable} lead="This list may be incomplete." />
      ) : null}
    </>
  );
}

export function NeedsYouNow({ summary }: Readonly<{ summary: StaffAttentionSummary }>) {
  const [tab, setTab] = useState<NeedsTab>("all");
  const total = useMemo(() => needsRows(needsReasons(summary), "all").length, [summary]);
  const lists = summary.sources.filter(
    (source) =>
      source.status === "ok" && source.severity !== "info" && (source.count > 0 || source.capped),
  );

  return (
    <section
      aria-labelledby="home-needs"
      className="rounded-lg border border-hairline bg-card p-2 text-card-foreground"
    >
      <Tabs value={tab} onValueChange={(value) => setTab(value as NeedsTab)} className="gap-1">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-2.5 pr-2.5 pl-3.5">
          <h2 id="home-needs" className="m-0 flex items-baseline gap-2.5">
            <span className="font-serif text-[22px] leading-7 font-medium">Needs you now</span>
            {/* The API names only the first customers of each list, so the number is exact only when nothing is cut or blind. */}
            <span className="text-sm font-medium text-muted-foreground">
              {total}
              {isPartial(summary) ? " shown" : ""}
            </span>
          </h2>
          <TabsList aria-label="Show">
            {(Object.keys(tabLabels) as NeedsTab[]).map((key) => (
              <TabsTrigger key={key} value={key}>
                {tabLabels[key]}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        <TabsContent value={tab}>
          <TabBody summary={summary} tab={tab} lists={lists} />
        </TabsContent>
      </Tabs>
      <FullLists sources={lists} />
    </section>
  );
}
